import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { customerReviews as allReviews } from "../../data/customerReviews";

const ORANGE = "#F68C43";

const Star = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={{ display: "block" }}>
    <path
      fill={ORANGE}
      d="M12 1.8l3.1 6.9 7.5.8-5.6 5 1.6 7.4L12 17.9 5.4 21.9 7 14.5l-5.6-5 7.5-.8z"
      stroke={ORANGE}
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  </svg>
);

const Stars = ({ size = 22, gap = 3 }) => (
  <div style={{ display: "flex", gap }}>
    {[0, 1, 2, 3, 4].map((i) => (
      <Star key={i} size={size} />
    ))}
  </div>
);

const Quote = () => (
  <svg width="38" height="30" viewBox="0 0 38 30" style={{ display: "block" }}>
    <path
      fill="#1E3F8F"
      d="M0 30V17C0 7.5 5 1.5 14 0l1.6 4.6C10.6 6 8.6 9 8.4 13H16v17H0zm21 0V17C21 7.5 26 1.5 35 0l1.6 4.6C31.6 6 29.6 9 29.4 13H37v17H21z"
    />
  </svg>
);

const GoogleLogoSvg = ({ size = 24 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} xmlns="http://www.w3.org/2000/svg" style={{ display: "block" }}>
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

export default function Testimonials({
  intro = "Pick Pack Pro maintains a 5.0-star Google rating based on verified customer reviews. Read real feedback from Amazon sellers, ecommerce brands, and multichannel retailers who trust our fulfilment services.",
  reviewsUrl = "/our-reviews"
}: any) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const fit = () => {
      if (wrapRef.current) {
        // Find container width instead of window width to prevent horizontal scrollbars on smaller screens
        const parentWidth = wrapRef.current.parentElement?.offsetWidth || window.innerWidth;
        const newScale = Math.min(1, parentWidth / 1600);
        setScale(newScale);
      }
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);

  return (
    <section id="customer-reviews-home" style={{ width: "100%", background: "#01030B", display: "flex", justifyContent: "center", padding: 0 }}>
      <div
        ref={wrapRef}
        style={{ width: "100%", maxWidth: 1600, height: 616 * scale, position: "relative", overflow: "hidden" }}
      >
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap');

          @keyframes pppReviewMarquee {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(-50%);
            }
          }

          .ppp-reviews-marquee-track {
            display: flex;
            gap: 20px;
            width: max-content;
            animation: pppReviewMarquee 38s linear infinite;
          }

          .ppp-reviews-viewport:hover .ppp-reviews-marquee-track {
            animation-play-state: paused;
          }
        `}</style>

        <div
          style={{
            position: "absolute",
            left: "50%",
            top: 0,
            width: 1600,
            height: 616,
            marginLeft: -800, // Center the 1600px container
            transform: `scale(${scale})`,
            transformOrigin: "top center",
            fontFamily: "'Montserrat', sans-serif",
            color: "#fff",
            background:
              "radial-gradient(ellipse at 8% 50%, #081A54 0%, rgba(8,26,84,0) 38%), radial-gradient(ellipse at 90% 0%, #06173B 0%, rgba(6,23,59,0) 45%), linear-gradient(180deg,#020718 0%,#01030B 100%)",
            overflow: "hidden",
          }}
        >
          {/* warehouse shelving hint, top right */}
          <div
            style={{
              position: "absolute",
              right: 0,
              top: 0,
              width: 560,
              height: 300,
              opacity: 0.35,
              background:
                "repeating-linear-gradient(90deg, rgba(30,64,175,0.35) 0 6px, transparent 6px 90px), repeating-linear-gradient(0deg, rgba(120,90,40,0.25) 0 4px, transparent 4px 70px)",
              WebkitMaskImage: "linear-gradient(to left, #000 20%, transparent 100%)",
            }}
          />

          {/* swooshes */}
          <svg
            width="1600"
            height="616"
            viewBox="0 0 1600 616"
            style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
          >
            <defs>
              <linearGradient id="blueSwoosh" x1="0" y1="1" x2="1" y2="0">
                <stop offset="0" stopColor="#1D4ED8" stopOpacity="0.9" />
                <stop offset="1" stopColor="#1D4ED8" stopOpacity="0" />
              </linearGradient>
              <linearGradient id="orangeLine" x1="0" y1="1" x2="1" y2="0">
                <stop offset="0" stopColor="#F68C43" stopOpacity="0" />
                <stop offset="0.5" stopColor="#F68C43" stopOpacity="0.9" />
                <stop offset="1" stopColor="#F68C43" stopOpacity="0" />
              </linearGradient>
              <linearGradient id="orangeRight" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#F68C43" stopOpacity="0" />
                <stop offset="1" stopColor="#F68C43" stopOpacity="0.9" />
              </linearGradient>
            </defs>
            <path d="M0 0 C 260 40, 330 250, 150 616 L 0 616 Z" fill="url(#blueSwoosh)" opacity="0.55" />
            <path d="M0 40 C 250 -10, 340 220, 190 616" fill="none" stroke="#2563EB" strokeWidth="3" opacity="0.8" />
            <path d="M20 330 C 120 120, 420 40, 760 20" fill="none" stroke="url(#orangeLine)" strokeWidth="2" />
            <path d="M1200 616 C 1400 590, 1540 520, 1600 400" fill="none" stroke="url(#orangeRight)" strokeWidth="3" />
            <path d="M1300 616 C 1480 600, 1570 540, 1600 470 L1600 616 Z" fill="#B45309" opacity="0.35" />
          </svg>

          {/* eyebrow */}
          <div
            style={{
              position: "absolute",
              top: 68,
              left: 160,
              display: "flex",
              alignItems: "center",
              gap: 12,
              fontFamily: "var(--font-body, system-ui, sans-serif)",
              fontSize: "0.75rem",
              fontWeight: 700,
              letterSpacing: "1.5px",
              color: "#64748B",
              textTransform: "uppercase"
            }}
          >
            <span style={{ width: 24, height: 2, background: "#F68C43", flexShrink: 0 }} />
            PARTNER SUCCESS STORIES
          </div>

          {/* heading */}
          <h2
            style={{
              position: "absolute",
              top: 104,
              left: 160,
              width: 1280,
              margin: 0,
              textAlign: "left",
              fontSize: 40,
              fontWeight: 800,
              letterSpacing: "0.3px",
            }}
          >
            Rated <span style={{ color: ORANGE }}>5 Stars</span> on Google by Our Partners
          </h2>

          {/* paragraph */}
          <p
            style={{
              position: "absolute",
              top: 168,
              left: 160,
              width: 900,
              margin: 0,
              textAlign: "left",
              fontSize: 17.5,
              lineHeight: "27px",
              fontWeight: 500,
              color: "#E5E9F5",
            }}
          >
            {intro} <Link to={reviewsUrl} style={{ color: "#3B9BFF", textDecoration: "underline", marginLeft: 4 }}>View all reviews on Google &rarr;</Link>
          </p>

          

          {/* rating card */}
          <div
            style={{
              position: "absolute",
              left: 155,
              top: 260,
              width: 236,
              height: 258,
              borderRadius: 18,
              boxSizing: "border-box",
              background: "linear-gradient(180deg, rgba(17,40,99,0.85), rgba(7,20,56,0.92))",
              border: "1.5px solid transparent",
              backgroundClip: "padding-box",
              boxShadow:
                "0 0 0 1.5px #1D4ED8, inset 0 1px 0 rgba(246,140,67,0.9), 0 0 28px rgba(37,99,235,0.45)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "16px 14px",
            }}
          >
            <GoogleLogoSvg size={48} />
            <div style={{ fontSize: 44, fontWeight: 800, lineHeight: "48px", marginTop: 6 }}>5.0</div>
            <div style={{ marginTop: 8 }}>
              <Stars size={24} gap={3} />
            </div>
            <div style={{ marginTop: 8, fontSize: 14, fontWeight: 700, letterSpacing: "2.5px" }}>EXCELLENT</div>
            <div style={{ marginTop: 8, fontSize: 13, lineHeight: "18px", textAlign: "center", color: "#DDE3F3", fontWeight: 500 }}>
              Based on 100+
              <br />
              verified Google reviews
            </div>
          </div>

          {/* sliding review cards track (continuous marquee, pause on hover) */}
          <div
            className="ppp-reviews-viewport"
            style={{
              position: "absolute",
              left: 420,
              top: 260,
              width: 1040,
              height: 265,
              overflow: "hidden",
              WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 4%, black 96%, transparent 100%)",
              maskImage: "linear-gradient(to right, transparent 0%, black 4%, black 96%, transparent 100%)",
            }}
          >
            <div className="ppp-reviews-marquee-track">
              {[...allReviews, ...allReviews].map((r, i) => {
                const clampedText = r.text.length > 160 ? r.text.slice(0, 155) + "..." : r.text;
                return (
                  <div
                    key={i + "-" + r.name}
                    style={{
                      width: 295,
                      height: 258,
                      boxSizing: "border-box",
                      borderRadius: 18,
                      background: "linear-gradient(180deg, rgba(14,30,76,0.85), rgba(6,17,48,0.9))",
                      border: "1px solid #14307A",
                      boxShadow: "inset 0 1px 0 rgba(96,140,255,0.18)",
                      position: "relative",
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        left: 22,
                        top: 22,
                        width: 44,
                        height: 44,
                        borderRadius: "50%",
                        background: r.color || "#3B82F6",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 600,
                        fontSize: r.initials.length > 1 ? 16 : 19,
                      }}
                    >
                      {r.initials}
                    </div>
                    <div style={{ position: "absolute", left: 78, top: 24 }}>
                      <div style={{ fontSize: 16, fontWeight: 600 }}>{r.name}</div>
                      <div style={{ fontSize: 13, color: "#CBD3E8", marginTop: 3, fontWeight: 500 }}>Google Review</div>
                    </div>
                    <div style={{ position: "absolute", right: 22, top: 25 }}>
                      <GoogleLogoSvg size={26} />
                    </div>
                    <div style={{ position: "absolute", left: 22, top: 78 }}>
                      <Stars size={20} gap={3} />
                    </div>
                    <div style={{ position: "absolute", right: 22, top: 72 }}>
                      <Quote />
                    </div>
                    <p
                      style={{
                        position: "absolute",
                        left: 22,
                        top: 112,
                        width: 251,
                        margin: 0,
                        fontSize: 14.5,
                        lineHeight: "22px",
                        fontWeight: 500,
                        color: "#EEF1FA",
                      }}
                    >
                      {clampedText}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}



